<template>
  <div class="create-auction-form grey-box-container">
    <h2>Summary</h2>

    <div class="summary-container">
      <h3>Property Information</h3>

      <div class="clearfix">
        <div class="col1">Type</div>
        <div class="col2">{{ propertyTypeLabel(auction.propertyType) }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Name</div>
        <div class="col2">{{ auction.name }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">APN</div>
        <div class="col2">{{ auctionMetadata.apn }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Address</div>
        <div class="col2">{{ auction.address }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Zip</div>
        <div class="col2">{{ auction.zip }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">City</div>
        <div class="col2">{{ auction.city }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">State</div>
        <div class="col2">{{ auction.state }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Import source</div>
        <div class="col2">{{ auctionMetadata.source }} {{ auctionMetadata.sourceId }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Description</div>
        <div class="col2 markdown">
          <vue-markdown :source="auctionMetadata.description" />
        </div>
      </div>
      <div class="clearfix margin">
        <div class="col1">Photos</div>
        <div class="col2">
          <div v-for="photo in auctionMetadata.photos" :key="photo" class="photos" v-if="auctionMetadata.photosRemoved.indexOf(photo) === -1">
            <img :src="photo" />
          </div>
          <div v-for="photo in auctionMetadata.photosAdded" :key="photo.name" class="photos" v-if="!photo.removed">
            <img :src="photo.dataUrl" />
          </div>
          <div v-if="!auctionMetadata.photos || auctionMetadata.photos.length === 0" class="photos">
            <img src="../assets/placeholder-photos.jpg" />
          </div><br/>
        </div>
      </div>
      <div class="clearfix margin">
        <div class="col1">Documents</div>
        <div class="col2">
          <div v-for="document in auctionMetadata.documents" :key="document.url">
            {{ document.name }}<br/>
            <a target="_blank" :href="document.url"><small>{{ document.url }}</small></a>
          </div>
          <div v-if="!auctionMetadata.documents || auctionMetadata.documents.length === 0">
            No documents
          </div>
        </div>
      </div>
      <div class="clearfix margin">
        <div class="col1">Geolocation</div>
        <div class="col2">
          <iframe v-if="validCoordinates" :src="googleMapsSrc" class="map" height="250" frameborder="0" />
        </div>
      </div>
      <div>Document Vault</div>
      <div>
        <CreateListingFileUpload v-if="!isAuctionEdit" :addFiles="false" />
        <FileBox :auctionId="auction.id" usecase="auction" :mutable="false" v-else />
      </div>
    </div>

    <div class="summary-container">
      <h3>Auction Information</h3>

      <div class="clearfix">
        <div class="col1">Publish date</div>
        <div class="col2">{{ auction.publishDate | prettyDateTime(auctionMetadata.timezone) }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Registration closing</div>
        <div class="col2">{{ auction.freezeDate | prettyDateTime(auctionMetadata.timezone) }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Auction start date</div>
        <div class="col2">{{ auction.startDate | prettyDateTime(auctionMetadata.timezone) }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Auction close date</div>
        <div class="col2">{{ auction.endDate | prettyDateTime(auctionMetadata.timezone) }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Starting price</div>
        <div class="col2">{{ auction.startingPrice | prettyMoney }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Reserve price</div>
        <div class="col2">{{ auction.reservePrice | prettyMoney }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Minimum increment</div>
        <div class="col2">{{ auction.minimumIncrement | prettyMoney }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Autobids allowed</div>
        <div class="col2">{{ auctionMetadata.allowAutobids ? 'Yes, autobids are allowed': 'No autobids' }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Promote property in global PFX website</div>
        <div class="col2">{{ auctionMetadata.globalVisibility ? 'Yes, promote on global PFX': 'No, limit to company subsection' }}</div>
      </div>
      <div class="clearfix margin">
        <div class="col1">Additional information</div>
        <div class="col2 markdown">
          <vue-markdown :source="auctionMetadata.auctionInfo" />
        </div>
      </div>
      <div class="clearfix">
        <div class="col1">Commission information</div>
        <div class="col2">
          <div>{{ auctionMetadata.commissionInfo }}</div>
        </div>
      </div>
    </div>

    <div class="summary-container">
      <h3>Collaborators</h3>

      <div class="clearfix">
        <div class="col1">Listing Agent</div>
        <div class="col2">{{ listingAgentName }}</div>
      </div>
      <div class="clearfix">
        <div class="col1">Seller</div>
        <div class="col2" v-if="sellerProfile.id">{{ sellerProfile.email }} (Accepted)</div>
        <div class="col2" v-else-if="auction.sellerEmail">{{ auction.sellerEmail }} (Invitation)</div>
        <div class="col2" v-else>(No seller)</div>
      </div>
    </div>

    <div class="summary-container">
      <h3>Buyers Requirements</h3>

      <div class="clearfix">
        <div class="col1">Instructions</div>
        <div class="col2 markdown">
          <vue-markdown :source="auctionMetadata.instructions" />
        </div>
      </div>
      <div class="clearfix">
        <div class="col1">Required uploads</div>
        <div class="col2">
          <ul class="list-bullets">
            <li v-for="req in buyerRequirements" :key="req">{{ getDocLabel(req) }}</li>
          </ul>
        </div>
      </div>
      <div class="clearfix">
        <div class="col1">Personal message</div>
        <div class="col2 markdown">
          <vue-markdown :source="auctionMetadata.messageToBuyer" />
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import VueMarkdown from 'vue-markdown'
  import { propertyTypeLabel, fileDocTypeLabel } from '../utils/PfxUtils.js'
  import CreateListingFileUpload from './CreateListingFileUpload.vue'
  import FileBox from './elements/FileBox.vue'
  import { mapGetters } from 'vuex'

  export default {
    name: 'CreateListingSummary',
    components: {
      VueMarkdown,
      CreateListingFileUpload,
      FileBox
    },
    data() {
      return {
        listingAgentName: '',
        auction: {},
        auctionMetadata: {},
        sellerProfile: {},
        buyerRequirements: {},
        buyerRequirementOptions: []
      }
    },
    computed: {
      ...mapGetters([
        'isAuctionEdit'
      ]),
      validCoordinates() {
        return (
          this.auctionMetadata.lat >= -90 && this.auctionMetadata.lng >= -180
        )
      },
      googleMapsSrc() {
        return `https://www.google.com/maps/embed/v1/place?zoom=18&key=${process.env.config.google.apikey}&q=${this.auctionMetadata.lat},${this.auctionMetadata.lng}`
      }
    },
    created() {
      this.auction = this.$store.state.newAuctionStorage.auction
      this.auctionMetadata = this.$store.state.newAuctionStorage.auctionMetadata
      this.buyerRequirements = this.$store.state.newAuctionStorage.buyerRequirements
      this.sellerProfile = this.$store.state.newAuctionStorage.sellerProfile
      this.buyerRequirementOptions = this.$store.state.newAuctionStorage.buyerRequirementOptions
      this.fetchListingAgentProfile(this.auction.listingAgent)
    },
    methods: {
      propertyTypeLabel,
      fetchListingAgentProfile(userId) {
        this.listingAgentName = userId
        this.$PfxBackendApi.getUser(userId).then(res => {
          console.log('getUser (listingAgent) res', res)
          if (res.data) {
            this.listingAgentName = res.data.firstName + ' ' + res.data.lastName + ', DRE #' + res.data.dreNumber
          }
        }).catch(err => {
          console.error('getUser (listingAgent) err', err)
          this.$notify({ type: 'danger', message: 'Cannot fetch listing agent profile' })
        })
      },
      getDocLabel(id){
        return fileDocTypeLabel(id, this.buyerRequirementOptions)
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../pfx.scss';

  .col1 { @include column(1/4); }
  .col2 { @include column(3/4); }
  .col2 {
    word-break: break-all;
  }
  .photos {
    display: inline;
    img {
      height: 80px;
      padding: 0;
      margin: 0 6px 0 0;
    }
  }

  iframe.map {
    width: 100%;
    height: 250px;
    border: 0;
  }

  .clearfix {
    margin-bottom: 0.2rem;
  }
  .summary-container {
    margin-bottom: 5rem;
    &:last-child {
      margin-bottom: 0;
    }
  }
  .margin {
    margin-bottom: 1rem;
  }

  .markdown {
    //border: 1px solid #ccc;
    //padding: 1em;
    //background-color: #eee;
  }

</style>



// WEBPACK FOOTER //
// src/components/CreateListingSummary.vue